import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { useTheme } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IconButtonTypeMap } from '@mui/material/IconButton';

import { DashboardT } from 'business/definitions';
import IconMenu from 'components/IconMenu';
import ModalForm from 'components/Form/ModalForm';
import FolderForm from 'components/FolderForm/FolderForm';
import DashboardSendModal from 'components/DashboardSendModal/DashboardSendModal';
import DashboardPdfPreviewModal from 'components/PdfPreviewModal/DashboardPdfPreviewModal';
import ShareForm from 'components/ShareForm/ShareForm';
import { useShareDashboard } from 'components/ShareForm/hooks/share';
import { getPublicUrl } from 'components/ShareForm/helpers/publicUrl';
import { capitalize } from 'helpers/helpers';
import { trackEvent } from 'helpers/trackerHelpers';
import {
    useArchiveDashboard,
    useUpdateDashboard,
} from 'routes/AllDashboards/hooks/updateDashboard';
import { useDuplicateDashboard } from 'routes/AllDashboards/hooks/duplicateDashboard';
import { useCreateDashboard } from 'routes/AllDashboards/hooks/createDashboard';
import { ACTIVE_DASHBOARDS_QUERY_KEY } from 'routes/AllDashboards/hooks/getDashboardsList';
import { useSharingModalContext } from 'routes/Dashboard/components/SharingModalContext';
import { DASHBOARDS_PAGE_URL } from 'components/App/components/routes';
import {
    DASHBOARD_ACTION_TYPE,
    DashboardActionType,
    sortActionsCategories,
} from './helpers/actions';
import { usePossibleDashboardActions } from './hooks/actions';
import DashboardEditMenuCategory from './DashboardEditMenuCategory';
import useUpdateDashboardWidth from 'hooks/useUpdateDashboardWidth';
import DashboardPanelDetails from 'components/DashboardPanelDetails/DashboardPanelDetails';

type DashboardEditMenuProps = {
    show?: boolean;
    small?: boolean;
    color?: IconButtonTypeMap['props']['color'];
    dashboard: DashboardT;
    allowMove?: boolean;
    onClose?: (action: DashboardActionType | null) => void;
    // If isDashboardHeader is true, then setTitleEditMode will be called when
    // the rename button is clicked. Otherwise, a modal is opened.
    // Also, isDashboardHeader allows send and presentationMode actions
    isDashboardHeader?: boolean;
    setTitleEditMode?: (bool: boolean) => void;
    // The edit layout action is only made available when isDashboardHeader is true,
    // as shown in the getEditActions in the helpers
    toggleEditMode?: () => void;
    isInPresentationMode?: boolean;
    onPresentationModeChange?: () => void;
    isFavourite: boolean;
    onFavouriteChange: () => void;
    setIgnoreNextBlurTrue?: () => void;
};

const DashboardEditMenu = ({
    show = true,
    small = false,
    color = 'default',
    dashboard,
    allowMove = false,
    onClose = () => {},
    isDashboardHeader = false,
    setTitleEditMode = undefined,
    toggleEditMode = () => {},
    isInPresentationMode = false,
    onPresentationModeChange = undefined,
    isFavourite,
    onFavouriteChange,
    setIgnoreNextBlurTrue = undefined,
}: DashboardEditMenuProps): JSX.Element => {
    const { t } = useTranslation();
    const theme = useTheme();
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const [selectedAction, setSelectedAction] =
        useState<DashboardActionType | null>(null);
    const [openPDFPreview, setOpenPDFPreview] = useState<boolean>(false);
    const [openPanelDetails, setOpenPanelDetails] = useState<boolean>(false);

    const { isSharingModalOpen } = useSharingModalContext();

    useEffect(() => {
        if (isSharingModalOpen) {
            setSelectedAction(DASHBOARD_ACTION_TYPE.Share);
        }
    }, [isSharingModalOpen]);

    const closeMenu = () => {
        setSelectedAction(null);
        if (onClose) onClose(selectedAction);
    };

    const duplicateDashboardMutation = useDuplicateDashboard();
    const duplicateDashboard = (updatedDashboard: { title: string }) => {
        duplicateDashboardMutation.mutate({
            title: updatedDashboard.title,
            key: dashboard.key,
        });
        closeMenu();
    };

    const createDashboardMutation = useCreateDashboard();
    const createDashboard = (data: { title: string }) => {
        createDashboardMutation.mutate({ title: data.title });
        closeMenu();
    };

    const activateTitleEditMode = () => {
        if (setIgnoreNextBlurTrue) setIgnoreNextBlurTrue();
        if (setTitleEditMode) setTitleEditMode(true);
        closeMenu();
    };

    const handleOpenPanelDetails = () => {
        setOpenPanelDetails(true);
        closeMenu();
    };

    const updateDashboardMutation = useUpdateDashboard(() => {
        queryClient.invalidateQueries({
            queryKey: [ACTIVE_DASHBOARDS_QUERY_KEY],
        });
    });

    const renameDashboard = ({ title }: { title: string }) => {
        if (title !== dashboard.title)
            updateDashboardMutation.mutate({
                newDashboardData: { title },
                key: dashboard.key,
                slug: dashboard.slug,
            });
        closeMenu();
    };

    const switchDashboardPresentationMode = () => {
        updateDashboardMutation.mutate({
            newDashboardData: { isInPresentationMode: !isInPresentationMode },
            key: dashboard.key,
            slug: dashboard.slug,
        });
        if (onPresentationModeChange) onPresentationModeChange();
        closeMenu();
    };

    const toggleFavourite = () => {
        const event = isFavourite
            ? 'decision/homepage/favorites/dashboard-removed'
            : 'decision/homepage/favorites/dashboard-added';
        trackEvent(event);
        onFavouriteChange();
        closeMenu();
    };

    const updateDashboardWidthMutation = useUpdateDashboardWidth(
        dashboard.key,
        dashboard.slug,
    );

    const toggleFullWidth = () => {
        updateDashboardWidthMutation.mutate({
            is_full_width: !dashboard.isFullWidth,
        });
        closeMenu();
    };

    const dashboardArchiveMutation = useArchiveDashboard(() => {
        trackEvent('decision/dashboard/options/archived-successfully');
        if (isDashboardHeader) navigate(DASHBOARDS_PAGE_URL);
    });
    const archiveDashboard = () => {
        dashboardArchiveMutation.mutate({ key: dashboard.key });
        closeMenu();
    };

    const { mutate: shareDashboard } = useShareDashboard();

    const onClickModal = (name: DashboardActionType | null) => () => {
        setSelectedAction(name);
        if (name === DASHBOARD_ACTION_TYPE.Rename && isDashboardHeader)
            activateTitleEditMode();
        if (name === DASHBOARD_ACTION_TYPE.PresentationMode)
            switchDashboardPresentationMode();
        if (name === DASHBOARD_ACTION_TYPE.Export) {
            trackEvent('decision/dashboard/options/pdf-export-button-click', {
                dashboard: dashboard.key,
                slug: dashboard.slug,
            });
            setOpenPDFPreview(true);
        }
        if (name === DASHBOARD_ACTION_TYPE.Send)
            trackEvent('decision/dashboard/options/pdf-send-button-click', {
                dashboard: dashboard.key,
            });
        if (name === DASHBOARD_ACTION_TYPE.Favourite) toggleFavourite();
        if (name === DASHBOARD_ACTION_TYPE.EditLayout) toggleEditMode();
        if (name === DASHBOARD_ACTION_TYPE.FullWidth) toggleFullWidth();
        if (name === DASHBOARD_ACTION_TYPE.Informations)
            handleOpenPanelDetails();
    };

    const possibleDashboardActionsByCategory = usePossibleDashboardActions(
        dashboard.canEdit,
        isDashboardHeader,
        allowMove,
    );
    const sortedActionsCategories = sortActionsCategories(
        possibleDashboardActionsByCategory,
    );

    const possibleDashboardActions = Object.values(
        possibleDashboardActionsByCategory,
    ).flat();

    const showModals = {} as Record<DashboardActionType, boolean>;

    possibleDashboardActions.forEach((action) => {
        showModals[action] = selectedAction === action;
    });

    const isShared =
        dashboard.orgSharing.isPublic ||
        dashboard.orgSharing.isRestricted ||
        dashboard.usersSharing.length > 0;

    if (!show || possibleDashboardActions.length === 0) return <></>;

    return (
        <div className="header-options-wrapper">
            <IconMenu
                iconElement={<MoreVertIcon />}
                tooltip={capitalize(t('common.options'))}
                tooltipPlacement="bottom"
                small={small}
                className="e2e-dashboard-options-menu"
                color={color}
                data-testid={`dashboard-options-${dashboard.title}`}
            >
                {sortedActionsCategories.map((category, index) => {
                    const dashboardActions =
                        possibleDashboardActionsByCategory[category];
                    const isLast = index === sortedActionsCategories.length - 1;
                    return (
                        <DashboardEditMenuCategory
                            key={category}
                            actions={dashboardActions}
                            onClickModal={onClickModal}
                            isInPresentationMode={isInPresentationMode}
                            isFavourite={isFavourite}
                            isLast={isLast}
                            isFullWidth={dashboard.isFullWidth}
                        />
                    );
                })}
            </IconMenu>
            {openPDFPreview && (
                <DashboardPdfPreviewModal
                    openPDFPreview={openPDFPreview}
                    setOpenPDFPreview={setOpenPDFPreview}
                    dashboard={dashboard}
                />
            )}

            {showModals.create && (
                <ModalForm
                    show
                    closeModal={closeMenu}
                    submit={createDashboard}
                    submitText={t('common.add')}
                    title={t('dashboards.create')}
                    fields={[
                        {
                            key: 'title',
                            name: 'title',
                            label: t('common.title'),
                            type: 'text',
                            validators: ['required'],
                        },
                    ]}
                    submitClass="e2e-modal-add-dashboard-button"
                />
            )}

            {showModals.archive && (
                <ModalForm
                    show
                    closeModal={closeMenu}
                    submit={archiveDashboard}
                    submitText={t('common.archive')}
                    title={t('common.deleteConfirm')}
                    secondary={isShared && t('dashboards.deleteWarning')}
                    confirmButtonColor={theme.palette.red}
                    submitClass="e2e-delete-dashboard-button"
                />
            )}

            {showModals.duplicate && (
                <ModalForm
                    show
                    closeModal={closeMenu}
                    submit={duplicateDashboard}
                    submitText={t('common.duplicate')}
                    title={t('dashboards.duplicate')}
                    fields={[
                        {
                            key: 'title',
                            name: 'title',
                            label: t('common.title'),
                            type: 'text',
                            value: `${dashboard.title} - copie`,
                            validators: ['required'],
                        },
                    ]}
                    submitClass="e2e-modal-duplicate-dashboard-button"
                />
            )}
            {showModals.share && (
                <ShareForm
                    show
                    translationKey="dashboard"
                    shareKey={dashboard.key}
                    owner={dashboard.owner}
                    orgSharing={dashboard.orgSharing}
                    title={dashboard.title}
                    usersSharing={dashboard.usersSharing}
                    groupsSharing={dashboard.groupsSharing}
                    closeModal={closeMenu}
                    submit={shareDashboard}
                    publicUrl={getPublicUrl(dashboard)}
                    dashboard={dashboard}
                />
            )}

            {showModals.rename && !isDashboardHeader && (
                <ModalForm
                    show
                    closeModal={closeMenu}
                    submit={renameDashboard}
                    submitText={t('common.save')}
                    title={t('dashboards.update')}
                    fields={[
                        {
                            key: 'title',
                            name: 'title',
                            label: t('common.title'),
                            type: 'text',
                            value: dashboard.title,
                            validators: ['required'],
                        },
                    ]}
                    submitClass="e2e-modal-update-dashboard-button"
                />
            )}

            {showModals.move && (
                <FolderForm
                    show
                    closeModal={closeMenu}
                    title={t('dashboards.move')}
                    dashboard={dashboard}
                />
            )}

            {showModals.send && (
                <DashboardSendModal
                    dashboard={dashboard}
                    show
                    closeModal={closeMenu}
                />
            )}

            {openPanelDetails && (
                <DashboardPanelDetails
                    dashboard={dashboard}
                    open={openPanelDetails}
                    onClose={() => setOpenPanelDetails(false)}
                />
            )}
        </div>
    );
};

export default DashboardEditMenu;
